import React from 'react';
import { FieldError, FieldValues, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Checkbox } from './Checkbox';
import { Fieldset, InputErrorMessage, InputLabel } from './components';

interface ICheckListProps {
  name: string;
  className?: string;
  label?: string;
  id?: string;
  required?: boolean;
  error?: FieldError;
  touched?: boolean;
  checkboxes: {
    name: string;
    label: string;
    value: string;
  }[];
  register: UseFormRegister<FieldValues>;
  selectAll?: (isChecked: boolean) => void;
  selectAllValue?: boolean;
  selectAllLabel?: string;
}

const CheckList = ({ name, label, checkboxes, register, selectAll, ...rest }: ICheckListProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Fieldset className={rest.className}>
      <StyledInputLabel htmlFor={rest.id || name} required={Boolean(rest.required)}>
        {label}
      </StyledInputLabel>
      <ListContainer>
        {selectAll && (
          <CheckboxAll
            className="checkbox-item-all"
            type="checkbox"
            name="all"
            onChange={evt => selectAll(evt.target.checked)}
            checked={rest.selectAllValue}
            label={rest.selectAllLabel ?? t`commonComponents.checkList.all`}
          />
        )}
        {checkboxes.map(checkbox => (
          <Checkbox
            className="checkbox-item"
            key={`${checkbox.name}-${checkbox.value}`}
            id={`${checkbox.name}-${checkbox.value}`}
            name={checkbox.name}
            label={checkbox.label}
            value={checkbox.value}
            register={register}
            type="checkbox"
          />
        ))}
      </ListContainer>
      <InputErrorMessage error={rest.error} touched={rest.touched} />
    </Fieldset>
  );
};

export { CheckList };

const StyledInputLabel = styled(InputLabel)`
  margin-bottom: 18px;
`;

const ListContainer = styled.div`
  border-radius: 8px;
  border: 1px solid #dee2e6;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CheckboxAll = styled(Checkbox)`
  padding-bottom: 16px;
  border-bottom: 1px solid #dee2e6;
`;
